/**
 * 全站导航 —— **单一真源**（PC 侧栏 / 手机抽屉 / 「我的」页三处共用同一份）
 *
 * ── 为什么抽出来（2026-10-05）──
 *   新版原型要求：PC 五个入口（工作台 / 账号经营 / 内容工厂 / 发布与增长 / 知识与资产）、
 *   手机底部四个 Tab（工作台 / 内容工厂 / 发布与增长 / 我的），而**「我的」页要能看全所有入口**。
 *   三处如果各写一份，迟早漂移 —— 于是这里定义一次，三处 import。
 *
 * ── 三条不能破的约束（沿用 `AppShell.vue` 原来的纪律）──
 *  ① **旧链接一律不失效**：每个子项都指向**原有路径**（`/business`、`/benchmark`、`/topics`、
 *     `/tasks`、`/works`、`/deliverables`、`/remix`、`/distributions`、`/metrics`、`/assets`、
 *     `/skills`）。本轮只改**分组与组名**，路径一个没动。
 *  ② **子项文字尽量不改**：文案定位器遍布既有用例（`getByRole('button', {name:/写脚本/})` 之类），
 *     改一个词会连锁报红。本轮只改两个**组名**：内容生产 → 内容工厂、资源中心 → 知识与资产
 *     （`deploy/_e2e-p1-nav.mjs` 的 `ENTRANCES` 已同步改过）。
 *  ③ **手机上任何页面都要进得去**（BUG-12 的原始诉求）：抽屉不折叠，
 *     并且「我的」页也列同一份清单。
 */

export interface NavLeaf {
  key: string;
  label: string;
  icon: string;
  to: string;
}

export interface NavGroup {
  key: string;
  label: string;
  icon: string;
  to: string;
  children: NavLeaf[];
}

/** PC 侧栏 / 手机抽屉 / 我的 共用的五个业务入口 */
export const NAV_GROUPS: NavGroup[] = [
  {
    key: 'workbench',
    label: '工作台',
    icon: 'sparkle',
    to: '/',
    children: [],
  },
  {
    key: 'operation',
    label: '账号经营',
    icon: 'target',
    to: '/operation',
    children: [
      { key: 'operation', label: '经营账号', icon: 'target', to: '/operation' },
      { key: 'business', label: '生意档案', icon: 'folder', to: '/business' },
      { key: 'benchmark', label: '对标分析', icon: 'search', to: '/benchmark' },
    ],
  },
  {
    key: 'content',
    /** ★ 原型定名：内容工厂 */
    label: '内容工厂',
    icon: 'fileText',
    to: '/topics',
    children: [
      { key: 'topic', label: '选题与脚本', icon: 'fileText', to: '/topics' },
      { key: 'tasks', label: '生成中心', icon: 'clock', to: '/tasks' },
      /**
       * 内容任务（P2 首条链）：周计划条目 → 内容任务 → 脚本版本 → 审批 → 预算 → 媒体。
       * 与「生成中心」（出片子）不是一回事：这一条管的是"脚本与钱批没批"。
       */
      { key: 'content-tasks', label: '内容任务', icon: 'fileText', to: '/content-tasks' },
      { key: 'works', label: '作品库', icon: 'grid', to: '/works' },
      { key: 'deliverables', label: '交付成果', icon: 'fileText', to: '/deliverables' },
      { key: 'remix', label: '爆款复刻', icon: 'search', to: '/remix' },
      { key: 'agent', label: '智能体', icon: 'bot', to: '/agent' },
    ],
  },
  {
    key: 'growth',
    label: '发布与增长',
    icon: 'upload',
    to: '/distributions',
    children: [
      { key: 'distributions', label: '分发中心', icon: 'upload', to: '/distributions' },
      { key: 'metrics', label: '数据复盘', icon: 'chart', to: '/metrics' },
    ],
  },
  {
    key: 'resources',
    /** ★ 原型定名：知识与资产 */
    label: '知识与资产',
    icon: 'layers',
    to: '/assets',
    children: [
      { key: 'assets', label: '素材与角色', icon: 'layers', to: '/assets' },
      { key: 'skills', label: '工具（技能库）', icon: 'wand', to: '/skills' },
    ],
  },
];

/** 服务类入口（不属于 5 个业务入口，单独一组放在下面） */
export const SERVICE_NAV: NavLeaf[] = [{ key: 'support', label: '售后工单', icon: 'help', to: '/support' }];

/** 抽屉/「我的」里额外补的两个（侧栏没有单独列） */
export const EXTRA_NAV: NavLeaf[] = [
  { key: 'credits', label: '积分中心', icon: 'coins', to: '/credits' },
  { key: 'help', label: '帮助与规范', icon: 'help', to: '/help' },
];

export interface DrawerGroup {
  key: string;
  label: string;
  items: NavLeaf[];
}

/**
 * 分组清单（手机抽屉 + 「我的」页共用）。
 *
 * ★ 为什么带分组标题、而不是纯扁平列表：扁平列表里"账号经营""发布与增长"这些**入口名根本不出现**
 *   （只有子项），手机上就看不到和桌面同一套心智模型，`_e2e-p1-nav.mjs` 也会（正确地）判它缺入口。
 * ★ 仍然**不折叠**：折叠能省地方，但会重新制造"手机上某个页面进不去"的老问题。
 */
export function drawerGroupsOf(): DrawerGroup[] {
  return [
    ...NAV_GROUPS.map((g) => ({
      key: g.key,
      label: g.label,
      // 没有子项的入口（工作台）自己就是一条
      items: g.children.length ? g.children : [{ key: g.key, label: g.label, icon: g.icon, to: g.to }],
    })),
    { key: 'service', label: '服务与账号', items: [...SERVICE_NAV, ...EXTRA_NAV] },
  ];
}

/** 扁平化的全部入口（给"检查有没有漏"这类用途） */
export function allNavLeaves(): NavLeaf[] {
  return drawerGroupsOf().flatMap((g) => g.items);
}

/** 手机底部 Tab —— **按原型就是这 4 个**（`to` 用字符串，和 `isActive()` 的类型保持一致） */
export const MOBILE_TABS: NavLeaf[] = [
  { key: 'home', label: '工作台', icon: 'sparkle', to: '/' },
  { key: 'content', label: '内容工厂', icon: 'fileText', to: '/content-tasks' },
  { key: 'growth', label: '发布与增长', icon: 'upload', to: '/distributions' },
  { key: 'me', label: '我的', icon: 'users', to: '/me' },
];
